import React, { useRef, useMemo, useCallback } from 'react';
import VormatsAPI from '../../../../../common/apis/VormatsAPI';
import Spinner from '../../../../../common/components/Spinner';
import Toggle from '../../../../components/Toggle/Toggle';
import styles from '../../../../features/CompanySettings/FeaturesManagement/FeaturesList/components/CompanyFeatures/CompanyFeatures.module.scss';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import usePagination from '../../../../hooks/usePagination';
import Pagination from '../../../../components/Pagination/Pagination';
import SearchComponent from '../../../../components/SearchComponent/SearchComponent';
import RecordNotFound from '../../../../components/_empty_state/RecordNotFound';

const UserFeaturesList = ({ userId, companyId }) => {
    const [updatingFeatureId, setUpdatingFeatureId] = React.useState(null);
    const scrollableContentRef = useRef(null);
    const { t } = useTranslation();

    const getUserFeatures = useCallback(async (params) => {
        const pageOptions = {
            page: params.page,
            perPage: params.perPage,
        };
        const listOptions = {
            search: params.search,
        };
        const data = await VormatsAPI.listUserFeatures(userId, companyId, pageOptions, listOptions, true);
        return {
            data: data?.data || [],
            meta: { total: data?.meta?.total || data?.total || data?.length || 0 },
        };
    }, [userId, companyId]);

    const {
        list: features,
        query,
        totalData,
        isLoading,
        onSearch,
        onChangePage,
        loadList,
    } = usePagination({
        service: getUserFeatures,
        itemsPerPage: 3,
        sortByFiled: 'featureId',
    });

    const handleToggle = async (feature) => {
        setUpdatingFeatureId(feature.featureId);
        try {
            await VormatsAPI.updateUserFeature(userId, {
                featureId: feature.featureId,
                enabled: feature.enabled ? 0 : 1,
                userId,
                value: feature.value || '',
            });
            await loadList();
        } finally {
            setUpdatingFeatureId(null);
        }
    };

    const handleValueChange = async (feature, value) => {
        setUpdatingFeatureId(feature.featureId);
        try {
            await VormatsAPI.updateUserFeature(userId, {
                featureId: feature.featureId,
                enabled: feature.enabled,
                userId,
                value,
            });
            await loadList();
        } finally {
            setUpdatingFeatureId(null);
        }
    };

    const isListScrollable = useMemo(() => {
        const { scrollHeight, offsetHeight } = scrollableContentRef?.current || {};
        return scrollHeight - offsetHeight > 0;
    }, [features]);

    return (
        features.length === 0 && !query.search ? null : (
            <div className={styles.wrapper} style={{ marginTop: 24 }}>
                <div className={styles.headerContainer}>
                    <h2 className={styles.header}>{t('company.settings.features.title')}</h2>
                    <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
                </div>
                <div className={styles.featuresTable}>
                    <div className={styles.featuresTableHeader}>
                        <div className={styles.itemHeader}>
                            <span>{t('company.settings.features.name')}</span>
                        </div>
                    </div>
                    {isLoading ? (
                        <div className={styles.spinnerWrapper}>
                            <Spinner size="3px" width="40px" height="40px" />
                        </div>
                    ) : features.length === 0 && query.search ? (
                        <RecordNotFound searchTerm={query.search} />
                    ) : features.length === 0 ? (
                        <RecordNotFound />
                    ) : (
                        <ul ref={scrollableContentRef} className={cx(styles.featureList, { [styles.scrollableFeatureList]: isListScrollable })}>
                            {features.map(feature => (
                                <li key={feature.featureId} className={styles.featureItem}>
                                    <span className={styles.featureName}>{feature.feature?.publicName || feature.feature?.name || feature.featureId}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        {typeof feature.value === 'string' && feature.feature?.value !== undefined && (
                                            <input
                                                type="text"
                                                value={feature.value || ''}
                                                onChange={e => handleValueChange(feature, e.target.value)}
                                                disabled={updatingFeatureId === feature.featureId}
                                                style={{ marginRight: 8, minWidth: 80 }}
                                            />
                                        )}
                                        <Toggle
                                            id={feature.featureId}
                                            checked={!!feature.enabled}
                                            onClick={e => e.stopPropagation()}
                                            onChange={() => handleToggle(feature)}
                                            className={styles.toggle}
                                            disabled={updatingFeatureId === feature.featureId}
                                        />
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {!!totalData && (
                    <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={onChangePage} />
                )}
            </div>
        )
    );
};

export default UserFeaturesList; 